import { Select } from '@primer/react';
import { GraphQLError } from 'graphql/index';
import React, { Suspense, useState } from 'react';
import { graphql, useMutation } from 'react-relay';

import useLazyLoadQuery from '../../../hooks/useLazyLoadQuery';
import useToast from '../../../hooks/useToast';
import { TaskOriginalUpdateDialog_taskOriginalUpdateMutation } from '../../../relay/__generated__/TaskOriginalUpdateDialog_taskOriginalUpdateMutation.graphql';
import { TaskOriginalUpdateDialog_taskQuery } from '../../../relay/__generated__/TaskOriginalUpdateDialog_taskQuery.graphql';
import { parseGraphQLError } from '../../../utils/error';
import Button from '../../core/Button';
import Checkbox from '../../core/Checkbox';
import Dialog, { DialogProps } from '../../core/Dialog';
import NumberInput from '../../core/NumberInput';
import Spinner from '../../core/Spinner';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import View from '../../core/View';
import TaskOriginalUpdateNoticeList from '../TaskOriginalUpdateNoticeList';

const taskForTaskOriginalUpdateDialog = graphql`
  query TaskOriginalUpdateDialog_taskQuery($id: ID!) {
    task(id: $id) {
      id
      sequence
      originalTask {
        sequence
      }
      problem {
        problemType
        creationType
      }
      firstChildren: derivedTasks(kind: "first") {
        id
        sequence
      }
      secondChildren: derivedTasks(kind: "second") {
        id
        sequence
      }
      originalUpdateSiblingCandidates {
        id
        sequence
        originalTask {
          sequence
        }
      }
    }
  }
`;

const CREATION_TYPES = [
  { key: 'first', label: '1단계' },
  { key: 'second', label: '2단계' },
] as const;

type CreationType = (typeof CREATION_TYPES)[number]['key'];
type ChildGroup = { originalTaskSequence: string; newCreationType: CreationType };

type Props = { taskId: string } & DialogProps;

const TaskOriginalUpdateDialog = ({ taskId, ...props }: Props) => (
  <Dialog {...props}>
    <Dialog.Header>
      <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>원본 문항 수정하기</Text>
    </Dialog.Header>
    {props.isOpen ? (
      <Suspense
        fallback={
          <View padding={3}>
            <Spinner size={'small'} />
          </View>
        }
      >
        <TaskOriginalUpdateForm taskId={taskId} onDismiss={props.onDismiss} />
      </Suspense>
    ) : null}
  </Dialog>
);

const CreationTypeSelect = ({ value, onChange }: { value: CreationType; onChange: (value: CreationType) => void }) => (
  <Select value={value} onChange={(e) => onChange(e.target.value as CreationType)}>
    {CREATION_TYPES.map(({ key, label }) => (
      <Select.Option key={key} value={key}>
        {label}
      </Select.Option>
    ))}
  </Select>
);

const TaskOriginalUpdateForm = ({ taskId, onDismiss }: { taskId: string; onDismiss?: () => void }) => {
  const [{ task }] = useLazyLoadQuery<TaskOriginalUpdateDialog_taskQuery>(
    taskForTaskOriginalUpdateDialog,
    { id: taskId },
    { fetchPolicy: 'network-only' },
  );
  const { toast } = useToast();
  const { originalTask, problem, firstChildren, secondChildren, originalUpdateSiblingCandidates } = task;
  const initialCreationType: CreationType = problem?.creationType === 'second' ? 'second' : 'first';

  const [originalTaskSequence, setOriginalTaskSequence] = useState(`${originalTask?.sequence ?? ''}`);
  const [creationType, setCreationType] = useState<CreationType>(initialCreationType);
  const [childGroups, setChildGroups] = useState<Partial<Record<CreationType, ChildGroup>>>({});
  const [siblings, setSiblings] = useState<string[]>([]);

  const [commit, isInFlight] = useMutation<TaskOriginalUpdateDialog_taskOriginalUpdateMutation>(graphql`
    mutation TaskOriginalUpdateDialog_taskOriginalUpdateMutation($input: TaskOriginalUpdateInput!) {
      taskOriginalUpdate(input: $input) {
        task {
          id
          title
          problem {
            id
            creationType
          }
          ...TaskDescriptionList_task
          ...TaskActionsMutationButtonStack_task
        }
        republishedTasks {
          id
          sequence
          ...TaskStatusLabel_task
        }
      }
    }
  `);

  // 대표(원본·2단계)를 1단계로 내릴 때만 자식을 함께 옮긴다
  const isDemoting = initialCreationType === 'second' && creationType === 'first';
  const childrenByCreationType: [CreationType, readonly { id: string; sequence: number }[]][] = [
    ['first', firstChildren],
    ['second', secondChildren],
  ];
  const movingChildren = isDemoting ? childrenByCreationType.filter(([, children]) => children.length > 0) : [];
  // 자동 생성 문제를 2단계로 지정할 때 같은 생성기 결과물을 이 문제의 1단계로 옮길 수 있다
  const canMoveSiblings = problem?.problemType === 'problem_generator' && creationType === 'second';

  const childGroupOf = (kind: CreationType): ChildGroup =>
    childGroups[kind] ?? { originalTaskSequence, newCreationType: kind };

  const save = () => {
    if (!originalTaskSequence) {
      toast('원본 문제 번호를 입력해 주세요', 'error');
      return;
    }
    commit({
      variables: {
        input: {
          task: taskId,
          originalTaskSequence: Number(originalTaskSequence),
          creationType,
          childGroups: movingChildren.map(([kind]) => ({
            creationType: kind,
            originalTaskSequence: Number(childGroupOf(kind).originalTaskSequence),
            newCreationType: childGroupOf(kind).newCreationType,
          })),
          generatedSiblings: canMoveSiblings ? siblings : [],
        },
      },
      onCompleted: ({ taskOriginalUpdate }) => {
        const republished = taskOriginalUpdate.republishedTasks.map(({ sequence }) => sequence);
        toast(
          republished.length > 0
            ? `원본 문항을 바꾸고 ${republished.length}건을 수학대왕에 재출시했어요 (${republished.join(', ')})`
            : '원본 문항을 바꿨어요',
          'success',
        );
        onDismiss?.();
      },
      onError: (error) => {
        toast(parseGraphQLError(error as GraphQLError)?.[0].message ?? '원본 문항을 바꾸지 못했어요', 'error');
      },
    });
  };

  return (
    <>
      <Dialog.Body>
        <Stack gapX={3} alignItems={'flex-end'}>
          <Stack.Item>
            <Text fontWeight={'bold'}>
              원본 문제 번호{' '}
              <Text fontSize={1} fontWeight={'normal'} color={'fg.muted'}>
                (현재:{originalTask?.sequence ?? '-'})
              </Text>
            </Text>
            <View>
              <NumberInput
                value={originalTaskSequence}
                onChange={(e) => setOriginalTaskSequence(e.target.value)}
                placeholder={'sequence'}
                skipDebounce
              />
            </View>
          </Stack.Item>
          <Stack.Item>
            <Text fontWeight={'bold'}>변형 정도</Text>
            <View>
              <CreationTypeSelect value={creationType} onChange={setCreationType} />
            </View>
          </Stack.Item>
        </Stack>
        <View sx={{ marginTop: 1 }}>
          <Text fontSize={1} color={'fg.muted'}>
            유형 대표(원본·2단계) 문제만
          </Text>
        </View>
        <TaskOriginalUpdateNoticeList
          notices={[
            '이 문항과 자식 문항을 포함하여 제목의 [복제] 태그 수 변경',
            '이 문항과 자식 문항을 포함하여 출시된 문제를 내게 검수 단계로 배정 후 재출시',
            '마이그레이션 짝 동기화',
          ]}
        />

        {movingChildren.length > 0 ? (
          <View sx={{ marginTop: 4 }}>
            <Text as={'p'} fontWeight={'bold'}>
              자식 문항 옮기기
            </Text>
            {movingChildren.map(([kind, children]) => (
              <View key={kind} sx={{ marginTop: 2 }}>
                <Stack gapX={2}>
                  <Stack.Item>
                    <Text fontSize={1} sx={{ whiteSpace: 'nowrap' }}>
                      {kind === 'first' ? '1단계' : '2단계'} {children.length}건 →
                    </Text>
                  </Stack.Item>
                  <Stack.Item>
                    <NumberInput
                      value={childGroupOf(kind).originalTaskSequence}
                      onChange={(e) =>
                        setChildGroups((groups) => ({
                          ...groups,
                          [kind]: { ...childGroupOf(kind), originalTaskSequence: e.target.value },
                        }))
                      }
                      placeholder={'원본 sequence'}
                      skipDebounce
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <CreationTypeSelect
                      value={childGroupOf(kind).newCreationType}
                      onChange={(value) =>
                        setChildGroups((groups) => ({
                          ...groups,
                          [kind]: { ...childGroupOf(kind), newCreationType: value },
                        }))
                      }
                    />
                  </Stack.Item>
                </Stack>
                <Text as={'p'} fontSize={0} color={'fg.muted'}>
                  {children.map(({ sequence }) => sequence).join(' ')}
                </Text>
              </View>
            ))}
          </View>
        ) : null}

        {canMoveSiblings && originalUpdateSiblingCandidates.length > 0 ? (
          <View sx={{ marginTop: 4 }}>
            <Text as={'p'} fontWeight={'bold'}>
              이 문제의 1단계로 옮길 같은 생성기 결과물
            </Text>
            <View sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {originalUpdateSiblingCandidates.map((sibling) => (
                <Stack key={sibling.id} gapX={2}>
                  <Stack.Item>
                    <Checkbox
                      checked={siblings.includes(sibling.id)}
                      onChange={(e) =>
                        setSiblings((ids) =>
                          e.target.checked ? [...ids, sibling.id] : ids.filter((id) => id !== sibling.id),
                        )
                      }
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Text fontSize={1}>
                      {sibling.sequence} (지금 원본 {sibling.originalTask?.sequence ?? '-'})
                    </Text>
                  </Stack.Item>
                </Stack>
              ))}
            </View>
          </View>
        ) : null}
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant={'primary'} size={'large'} disabled={isInFlight} onClick={() => save()}>
          저장하기
        </Button>
      </Dialog.Footer>
    </>
  );
};

export default TaskOriginalUpdateDialog;
